import type { PromotionCard } from './PromotionCardEditor';

export type PromotionChannel = 'whatsapp' | 'briefing';

export const PROMOTION_NO_SOURCE = '__no_source__';

export interface PromotionBriefing {
  headline: string;
  body: string;
  imageUrl: string | null;
}

export interface PromotionDefinition {
  name: string;
  channels: PromotionChannel[];
  briefing: PromotionBriefing | null;
  audience: {
    allUsers: boolean;
    sources: string[];
    workAccount: boolean;
    engagement: 'any' | 'medium' | 'high';
    userIds: string[];
    requiredFlags: string[];
    inviteCooldownDays: number | null;
    newUsersOnly: boolean;
  };
  holdoutPercent: number;
  whatsapp: {
    guidance: string;
    facts: string[];
    fallbackText: string;
    assistantNote: string;
    cardOnly?: boolean;
  };
  link:
    | { kind: 'url'; url: string }
    | { kind: 'invite'; button?: boolean }
    | null;
  followThrough:
    | { kind: 'info' }
    | { kind: 'invites' }
    | { kind: 'use_case'; useCase: string };
  outcome: 'link_clicked' | 'positive_reply';
  afterOnboardingDays: number | null;
  startDate: string;
  endDate: string;
  slot: {
    weekdays: number[];
    partsOfDay: ('morning' | 'afternoon' | 'evening')[];
  };
  card: PromotionCard | null;
}

export interface PromotionCounts {
  audience: number;
  holdout: number;
  scheduled: number;
  sent: number;
  skipped: number;
  failed: number;
  clicked: number;
}

export interface PromotionChannelCounts {
  sent: number;
  interested: number;
  declined: number;
}

export type PromotionChannelStats = Record<
  PromotionChannel,
  PromotionChannelCounts
>;

export interface PromotionSummary {
  slug: string;
  name: string;
  codeDefined?: boolean;
  status: 'draft' | 'launching' | 'live' | 'paused' | 'closed';
  launchingSince?: string | null;
  startDate: string;
  endDate: string;
  audience: PromotionDefinition['audience'];
  holdoutPercent: number;
  channel: 'whatsapp';
  channels: PromotionChannel[];
  channelStats: PromotionChannelStats;
  counts: PromotionCounts;
}

export const isInvitePromotion = (definition: PromotionDefinition) =>
  definition.link?.kind === 'invite' ||
  definition.followThrough.kind === 'invites';

export const isInviteButtonLink = (
  link: PromotionDefinition['link'] | null | undefined,
) => link?.kind === 'invite' && link.button === true;

export const promotionAudienceSummary = (
  audience: PromotionDefinition['audience'],
  holdoutPercent: number,
  sourceLabels: Record<string, string> = {},
) => {
  const sourceNames = audience.sources.map((source) =>
    source === PROMOTION_NO_SOURCE
      ? 'No recorded source'
      : (sourceLabels[source] ?? source),
  );
  const namedUsers = audience.userIds.length;
  const targetSummary = audience.allUsers
    ? 'All users'
    : [
        ...sourceNames,
        ...(namedUsers > 0
          ? [`${namedUsers} named user${namedUsers === 1 ? '' : 's'}`]
          : []),
      ].join(', ') || 'Nobody chosen yet';
  return [
    targetSummary,
    ...(audience.newUsersOnly ? ['new users only'] : []),
    ...(audience.workAccount ? ['work accounts only'] : []),
    ...(audience.engagement !== 'any'
      ? [`${audience.engagement} engagement`]
      : []),
    ...(audience.requiredFlags.length > 0
      ? [`needs ${audience.requiredFlags.join(', ')}`]
      : []),
    ...(holdoutPercent > 0 ? [`${holdoutPercent}% holdout`] : []),
  ].join(' · ');
};

export interface PromotionList {
  success: boolean;
  promotions: PromotionSummary[];
}

export interface PromotionPreview {
  success: boolean;
  eligible: number;
  skipped: Record<string, number>;
  sample: { userId: string; name: string | null }[];
  cardCost?: {
    outsideWindow: number;
    insideWindow: number;
    noWhatsApp: number;
    estimatedGbp: number;
    effectiveFrom: '2026-10-01';
    byMarket: { market: string; recipients: number; rateGbp: number }[];
  };
}

export interface PromotionDetail {
  success: boolean;
  promotion: PromotionSummary & { definition: PromotionDefinition };
  cohortSize?: number | null;
  sectionClicks?: Record<string, number>;
  funnel: {
    audience: number;
    holdout: number;
    scheduled: number;
    dispatched: number;
    sent: number;
    failed: number;
    skipped: Record<string, number>;
    outcomes: Record<string, number>;
    channelStats: PromotionChannelStats;
    clicked: number;
    adopted: number;
  };
  recipients: {
    id: string;
    userId: string;
    name: string | null;
    channel: PromotionChannel | null;
    status: string;
    scheduledFor: string | null;
    sentAt: string | null;
    skipReason: string | null;
    outcome: string;
    linkClickedAt: string | null;
    format: 'message' | 'card' | null;
  }[];
  cardTemplates?: {
    language: 'en' | 'fr' | 'de' | 'es' | 'it';
    status: 'not_submitted' | 'pending' | 'approved' | 'rejected';
    rejectionReason: string | null;
  }[];
  cardWarnings?: Record<
    string,
    { code: string; message: string }[] | undefined
  >;
}

export const stablePromotionJson = (value: unknown): string => {
  const sortObjectKeys = (current: unknown): unknown => {
    if (Array.isArray(current)) return current.map(sortObjectKeys);
    if (
      current !== null &&
      typeof current === 'object' &&
      (Object.getPrototypeOf(current) === Object.prototype ||
        Object.getPrototypeOf(current) === null)
    ) {
      return Object.fromEntries(
        Object.entries(current)
          .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
          .map(([key, nested]) => [key, sortObjectKeys(nested)] as const),
      );
    }
    return current;
  };

  return JSON.stringify(sortObjectKeys(value)) ?? 'undefined';
};

export interface PromotionOptions {
  success: boolean;
  sources: { key: string; label: string; group: string }[];
  flags: string[];
  useCases: { slug: string; title: string }[];
}

export interface PromotionDateInputs {
  startDate: string;
  endDate: string;
}

export const emptyPromotionChannelStats = (): PromotionChannelStats => ({
  whatsapp: { sent: 0, interested: 0, declined: 0 },
  briefing: { sent: 0, interested: 0, declined: 0 },
});

export const emptyDefinition = (): PromotionDefinition => {
  const date = promotionTodayInLondon();
  return {
    name: '',
    channels: ['whatsapp'],
    briefing: null,
    audience: {
      allUsers: false,
      sources: [],
      workAccount: false,
      engagement: 'any',
      userIds: [],
      requiredFlags: [],
      inviteCooldownDays: 30,
      newUsersOnly: false,
    },
    holdoutPercent: 0,
    whatsapp: {
      guidance: '',
      facts: [],
      fallbackText: '',
      assistantNote: '',
      cardOnly: false,
    },
    link: null,
    card: null,
    followThrough: { kind: 'info' },
    outcome: 'positive_reply',
    afterOnboardingDays: null,
    startDate: date,
    endDate: date,
    slot: { weekdays: [1, 2, 3, 4, 5], partsOfDay: ['morning'] },
  };
};

export const ukDate = (value: string | null) => {
  if (!value) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
};

export const formatUkDateInput = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

export const parseUkDateInput = (value: string): string | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const [, day, month, year] = match;
  if (Number(year) < 1) return null;
  const isoDate = `${year}-${month}-${day}`;
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ||
    date.toISOString().slice(0, 10) !== isoDate
    ? null
    : isoDate;
};

export const promotionTodayInLondon = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const values = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );
  return `${values.year}-${values.month}-${values.day}`;
};

export const formatPromotionShortDate = (value: string) => {
  const date = new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
      });
};

export const PROMOTION_SKIP_REASON_LABELS: Record<string, string> = {
  not_targeted: 'Not in the selected sources or IDs',
  no_work_account: 'No work account',
  not_high_engagement: 'Below the engagement level',
  feature_unavailable: 'Feature not available to them',
  opted_out: 'Opted out',
  recent_invite_message: 'Recent invite message',
  already_in_promotion: 'Already in this promotion',
  no_slot: 'No send slot left',
  cap: 'Skipped: limit',
  no_card: 'Skipped: no card',
  ended: 'Promotion ended',
  paused: 'Paused until it ended',
  closed: 'Promotion closed',
  no_credits: 'No credits',
};

export const promotionSkipReasonLabel = (reason: string) =>
  PROMOTION_SKIP_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');

export const promotionRecipientStatus = (
  status: string,
  skipReason: string | null,
) => {
  if (status === 'sent') return 'Sent';
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'holdout') return 'Holdout';
  if (status === 'skipped') {
    const reason = promotionSkipReasonLabel(skipReason ?? 'unknown');
    return reason.startsWith('Skipped:') ? reason : `Skipped: ${reason}`;
  }
  if (status === 'failed') {
    return `Failed: ${promotionSkipReasonLabel(skipReason ?? 'delivery_failed')}`;
  }
  return status
    .replace(/_/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase());
};

export const hasCardVariable = (value: string | null) =>
  value?.includes('{{') ?? false;

export const promotionChannelLabel = (channel: PromotionChannel) =>
  channel === 'whatsapp' ? 'WhatsApp' : 'Daily Briefing';

export const promotionLiveRows = (
  promotions: PromotionSummary[],
  today = promotionTodayInLondon(),
) =>
  promotions.filter(
    (promotion) =>
      promotion.status === 'launching' ||
      (promotion.status === 'live' && promotion.endDate >= today),
  );

const PROMOTION_LAUNCH_STALE_MS = 10 * 60_000;

export const promotionLaunchInterrupted = (
  promotion: PromotionSummary,
  now = Date.now(),
) =>
  promotion.status === 'launching' &&
  Boolean(promotion.launchingSince) &&
  now - new Date(promotion.launchingSince as string).getTime() >=
    PROMOTION_LAUNCH_STALE_MS;

export const promotionEndedRows = (
  promotions: PromotionSummary[],
  today = promotionTodayInLondon(),
) =>
  promotions.filter(
    (promotion) =>
      promotion.status === 'closed' ||
      promotion.status === 'paused' ||
      (promotion.status === 'live' && promotion.endDate < today),
  );

export const promotionDraftRows = (promotions: PromotionSummary[]) =>
  promotions.filter((promotion) => promotion.status === 'draft');
